// Verträge der Hülle. EINGEFROREN für die Oberfläche (web/shell): Felder nur additiv und optional ergänzen.
// Zeitpunkte sind ISO-8601-Texte (UTC), Geschäftstage 'YYYY-MM-DD'. Listen folgen '@shared/list'.
import { CHANNELS, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'

const zTimestamp = z.iso.datetime()
const zRecordRef = z.object({ type: z.string(), id: z.string() })

// ---- Suche -----------------------------------------------------------------------------------------------

export const zSearchHit = z.object({
  /** Art des Treffers ('item', 'partner', 'document' …) – bestimmt Darstellung und Ziel */
  entity: z.string(),
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  /** Kennung in Mono-Schrift (Artikelnummer, Belegnummer) */
  code: z.string().optional(),
  /** Unterart – bei Belegen die Belegart */
  kind: z.string().optional(),
  score: z.number().optional(),
})

export const searchQuery = defineContract({
  name: 'platform.search.query',
  title: 'Suchen',
  description: 'Durchsucht Artikel, Partner, Belege und alles Weitere, wofür das Leserecht besteht.',
  llm: {
    description:
      'Globale Suche über alle Arten von Datensätzen, die der Nutzer sehen darf. Verwenden, um einen Datensatz ' +
      'anhand von Name, Nummer oder Stichwort zu finden, wenn die Art noch nicht feststeht. Für gezielte Listen ' +
      'einer Art die jeweilige Listen-Aktion nehmen.',
    keywords: ['suchen', 'finden', 'wo ist', 'Artikelnummer', 'Belegnummer', 'Kunde'],
  },
  input: z.strictObject({
    q: z.string().trim().min(1).max(100),
    /** Treffer je Art */
    limit: z.int().min(1).max(50).default(8),
    /** nur diese Arten (Standard: alle erlaubten) */
    entities: z.array(z.string().min(1).max(60)).max(20).optional(),
  }),
  output: z.object({
    groups: z.array(z.object({ entity: z.string(), title: z.string(), hits: z.array(zSearchHit) })),
    total: z.int().nonnegative(),
  }),
  permission: '@authenticated',
  risk: 'read',
})

export const searchRebuild = defineContract({
  name: 'platform.search.rebuild',
  title: 'Suchindex neu aufbauen',
  description: 'Baut den Suchindex des Betriebs im Hintergrund neu auf – alle Arten oder nur die genannten.',
  llm: {
    description:
      'Merkt den Neuaufbau des Suchindex vor (läuft im Hintergrund). Verwenden, wenn die Suche Datensätze nicht ' +
      'findet, die es gibt, oder gelöschte noch anzeigt – etwa nach einem großen Import. Ändert keine Fachdaten.',
    keywords: ['Suchindex', 'Suche reparieren', 'neu indizieren', 'findet nichts'],
  },
  input: z.strictObject({
    /** nur diese Arten ('item', 'partner' …); ohne Angabe alle, für die ein Modul eine Quelle angemeldet hat */
    types: z.array(z.string().min(1).max(60)).max(50).optional(),
  }),
  output: z.object({
    /** false: Ein Neuaufbau für dieselben Arten wartet bereits */
    queued: z.boolean(),
  }),
  permission: 'platform.system.manage',
  risk: 'write',
  // Wartung für Verwalter – kein Werkzeug des Assistenten
  assistant: 'never',
})

// ---- Belege: wohin gehört ein Beleg? ----------------------------------------------------------------------

/**
 * Nennt zu einer Belegkennung die Belegart. Die Hülle leitet damit `/documents/:id` auf die Seite des Moduls, dem
 * die Belegart gehört (`entityLinks['document:<belegart>']`). Sichtbar mit dem Leserecht der Belegart am Standort
 * des Belegs (bei Umlagerungen: an einem der beiden Standorte) – sonst gibt es den Beleg für den Aufrufer nicht.
 */
export const documentLocate = defineContract({
  name: 'platform.document.locate',
  title: 'Beleg finden',
  description: 'Nennt zu einem Beleg Belegart, Nummer und Zustand.',
  llm: {
    description:
      'Liefert zu einer Belegkennung (UUID) die Belegart, die Belegnummer und den Zustand. Nur für die Oberfläche, ' +
      'die damit die richtige Seite öffnet; zum Lesen eines Belegs die Aktion der jeweiligen Belegart verwenden.',
    keywords: ['Beleg'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    id: zId,
    type: z.string(),
    /** deutsch: „Wareneingang“ */
    typeTitle: z.string(),
    number: z.string().nullable(),
    status: z.string(),
  }),
  permission: '@authenticated',
  risk: 'read',
  assistant: 'never',
})

// ---- Benachrichtigungen ------------------------------------------------------------------------------------

export const zNotification = z.object({
  id: zId,
  kind: z.string(),
  severity: z.enum(['info', 'success', 'warning', 'danger']),
  title: z.string(),
  body: z.string().nullable(),
  /** Pfad in der Oberfläche ('/stock/transfers/…') */
  link: z.string().nullable(),
  entity: zRecordRef.nullable(),
  locationId: zId.nullable(),
  generatedByAi: z.boolean(),
  createdAt: zTimestamp,
  readAt: zTimestamp.nullable(),
})

export const notificationList = defineContract({
  name: 'platform.notification.list',
  title: 'Benachrichtigungen anzeigen',
  description: 'Listet die eigenen Benachrichtigungen und die der eigenen Rollen, neueste zuerst.',
  llm: {
    description:
      'Listet Benachrichtigungen des Nutzers (auch die an seine Rollen). Verwenden, wenn nach Hinweisen, ' +
      'Meldungen oder „was gibt es Neues“ gefragt wird.',
    keywords: ['Benachrichtigung', 'Meldung', 'Hinweis', 'Neuigkeiten', 'Glocke'],
  },
  input: listInput({
    sortBy: ['createdAt'],
    defaultSort: { by: 'createdAt', dir: 'desc' },
    filters: { unreadOnly: z.boolean().default(false) },
  }),
  output: listOutput(zNotification).extend({ unreadCount: z.int().nonnegative() }),
  permission: '@authenticated',
  risk: 'read',
})

export const notificationMarkRead = defineContract({
  name: 'platform.notification.markRead',
  title: 'Benachrichtigungen als gelesen markieren',
  description: 'Markiert einzelne Benachrichtigungen als gelesen.',
  llm: { description: 'Markiert Benachrichtigungen als gelesen. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ ids: z.array(zId).min(1).max(200) }),
  output: z.object({ updated: z.int().nonnegative(), unreadCount: z.int().nonnegative() }),
  permission: '@authenticated',
  risk: 'write',
  assistant: 'never',
  audit: 'none',
})

export const notificationMarkAllRead = defineContract({
  name: 'platform.notification.markAllRead',
  title: 'Alle Benachrichtigungen als gelesen markieren',
  description: 'Markiert alle ungelesenen Benachrichtigungen als gelesen.',
  llm: { description: 'Markiert alle Benachrichtigungen als gelesen. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({}),
  output: z.object({ updated: z.int().nonnegative(), unreadCount: z.int().nonnegative() }),
  permission: '@authenticated',
  risk: 'write',
  assistant: 'never',
  audit: 'none',
})

// ---- Aufgaben und Klärfälle -----------------------------------------------------------------------------------

export const TASK_STATUSES = ['open', 'in_progress', 'done', 'dismissed'] as const

export const zTask = z.object({
  id: zId,
  kind: z.string(),
  title: z.string(),
  body: z.string().nullable(),
  status: z.enum(TASK_STATUSES),
  /** höher = dringender */
  priority: z.int(),
  dueDate: z.iso.date().nullable(),
  locationId: zId.nullable(),
  entity: zRecordRef.nullable(),
  assignedUserId: zId.nullable(),
  createdAt: zTimestamp,
  doneAt: zTimestamp.nullable(),
})

export const taskList = defineContract({
  name: 'platform.task.list',
  title: 'Aufgaben anzeigen',
  description: 'Listet Aufgaben und Klärfälle der Standorte, für die das Recht besteht.',
  llm: {
    description:
      'Listet Aufgaben und Klärfälle (Minusbestand, Transitdifferenzen, Freigaben …) je Standort. Verwenden, ' +
      'wenn gefragt wird, was zu tun oder zu klären ist. Ohne Angabe: offene Aufgaben, die dringendsten zuerst.',
    keywords: ['Aufgabe', 'Klärfall', 'offen', 'zu erledigen', 'To-do', 'fällig'],
  },
  input: listInput({
    sortBy: ['dueDate', 'createdAt', 'priority'],
    defaultSort: { by: 'dueDate', dir: 'asc' },
    filters: {
      /** open = offen und in Arbeit · closed = erledigt und verworfen */
      status: z.enum(['open', 'closed', 'all']).default('open'),
      locationId: zId.optional(),
      kind: z.string().min(1).max(60).optional(),
      assignedToMe: z.boolean().default(false),
    },
  }),
  output: listOutput(zTask),
  permission: 'platform.task.read',
  risk: 'read',
  scope: (input) => (input.locationId ? [input.locationId] : 'any'),
})

export const taskResolve = defineContract({
  name: 'platform.task.resolve',
  title: 'Aufgabe abschließen',
  description: 'Schließt eine Aufgabe als erledigt oder verwirft sie.',
  llm: {
    description:
      'Schließt eine offene Aufgabe: erledigt (done) oder verworfen (dismissed). Verwenden, wenn der Nutzer sagt, ' +
      'dass eine Aufgabe erledigt ist oder nicht mehr gilt. Die Aufgabe vorher über die Liste ermitteln.',
    keywords: ['erledigt', 'abhaken', 'verwerfen', 'Aufgabe schließen'],
  },
  input: z.strictObject({ id: zId, outcome: z.enum(['done', 'dismissed']).default('done') }),
  output: z.object({ id: zId, status: z.enum(TASK_STATUSES) }),
  permission: 'platform.task.resolve',
  risk: 'write',
  scope: 'record',
})

// ---- gespeicherte Ansichten -----------------------------------------------------------------------------------

/** Schlüssel einer Ansicht: '<seite>' oder '<seite>:<name>', z. B. 'stock.items' oder 'stock.items:Engpässe'. */
export const zViewKey = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[A-Za-z0-9][\p{L}\p{N} _.:/-]*$/u)

/** Inhalt einer Ansicht (Spalten, Sortierung, Filter, Dichte …) – frei für die Oberfläche, höchstens 20 000 Zeichen. */
export const zViewConfig = z
  .record(z.string(), z.json())
  .refine((config) => JSON.stringify(config).length <= 20_000, { error: 'Die Ansicht ist zu groß.' })

export const zView = z.object({ key: z.string(), config: z.record(z.string(), z.json()), updatedAt: zTimestamp })

export const viewList = defineContract({
  name: 'platform.view.list',
  title: 'Gespeicherte Ansichten laden',
  description: 'Listet die eigenen gespeicherten Tabellenansichten.',
  llm: { description: 'Lädt gespeicherte Tabellenansichten des Nutzers. Nur für die Oberfläche.', keywords: [] },
  input: listInput({
    sortBy: ['key'],
    filters: {
      /** nur Ansichten, deren Schlüssel so beginnt (z. B. 'stock.items') */
      prefix: z.string().min(1).max(120).optional(),
    },
  }),
  output: listOutput(zView),
  permission: '@authenticated',
  risk: 'read',
  assistant: 'never',
})

export const viewSave = defineContract({
  name: 'platform.view.save',
  title: 'Ansicht speichern',
  description: 'Speichert eine Tabellenansicht (legt sie an oder überschreibt sie).',
  llm: { description: 'Speichert eine Tabellenansicht des Nutzers. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ key: zViewKey, config: zViewConfig }),
  output: z.object({ key: z.string(), updatedAt: zTimestamp }),
  permission: '@authenticated',
  risk: 'write',
  assistant: 'never',
  audit: 'none',
})

export const viewDelete = defineContract({
  name: 'platform.view.delete',
  title: 'Ansicht löschen',
  description: 'Löscht eine gespeicherte Tabellenansicht.',
  llm: { description: 'Löscht eine gespeicherte Tabellenansicht des Nutzers. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ key: zViewKey }),
  output: z.object({ deleted: z.boolean() }),
  permission: '@authenticated',
  risk: 'write',
  assistant: 'never',
  audit: 'none',
})

// ---- Audit-Protokoll ---------------------------------------------------------------------------------------------

export const AUDIT_CHANNELS = [...CHANNELS, 'pos-offline'] as const

export const zAuditEntry = z.object({
  id: zId,
  at: zTimestamp,
  /** null = Systemlauf */
  actor: z.object({ id: zId, displayName: z.string() }).nullable(),
  channel: z.enum(AUDIT_CHANNELS),
  /** Name der Aktion ('inventory.adjustment.create') */
  action: z.string(),
  entity: zRecordRef,
  /** deutscher Klartext */
  summary: z.string(),
  reason: z.string().nullable(),
  /** Vorher/Nachher der geänderten Felder */
  changes: z.record(z.string(), z.object({ before: z.json(), after: z.json() })).nullable(),
  /** deutsche Bezeichnungen zu Feldnamen aus `changes`/`restrictedFields`, soweit das Modul der Aktion sie anmeldet */
  labels: z.record(z.string(), z.string()).optional(),
  locationId: zId.nullable(),
  /** über den KI-Assistenten ausgelöst */
  viaAssistant: z.boolean(),
  /** über den Support-Zugang des Betreibers ausgelöst (docs/anmeldung/SPEZIFIKATION.md §18) – fehlt bei älteren Servern */
  viaSupport: z.boolean().optional(),
  /**
   * true: vertraulicher Eintrag, für den dem Leser das Recht fehlt – `summary` ist dann ein Platzhalter,
   * `changes` und `reason` sind leer (wer, wann und welcher Datensatz bleiben sichtbar)
   */
  restricted: z.literal(true).optional(),
  /** Felder aus `changes`, die dem Leser fehlen, weil sie ein eigenes Recht verlangen (z. B. Einstandspreise) */
  restrictedFields: z.array(z.string()).optional(),
})

export const auditList = defineContract({
  name: 'platform.audit.list',
  title: 'Audit-Protokoll anzeigen',
  description: 'Zeigt, wer wann was über welchen Weg geändert hat – gefiltert nach Datensatz, Nutzer, Zeitraum, Kanal.',
  llm: {
    description:
      'Liest das Audit-Protokoll: wer hat wann was geändert, über welchen Kanal (Oberfläche, API, KI-Assistent). ' +
      'Verwenden für Fragen wie „Wer hat den Preis geändert?“ oder „Was hat der Assistent gestern gebucht?“. ' +
      'Für die Historie eines Datensatzes entityType und entityId angeben.',
    keywords: ['Protokoll', 'Historie', 'wer hat', 'geändert', 'Änderungen', 'nachvollziehen', 'Audit'],
  },
  input: listInput({
    sortBy: ['at'],
    defaultSort: { by: 'at', dir: 'desc' },
    filters: {
      /** Art des Datensatzes ('item', 'document' …); mit entityId: genau dieser Datensatz */
      entityType: z.string().min(1).max(80).optional(),
      entityId: zId.optional(),
      userId: zId.optional(),
      /** Zeitraum: from einschließlich, to ausschließlich */
      from: zTimestamp.optional(),
      to: zTimestamp.optional(),
      channel: z.enum(AUDIT_CHANNELS).optional(),
      action: z.string().min(1).max(120).optional(),
      locationId: zId.optional(),
    },
  }).refine((input) => !input.entityId || input.entityType, {
    path: ['entityType'],
    error: 'Zu einer Datensatz-ID gehört die Art des Datensatzes.',
  }),
  output: listOutput(zAuditEntry),
  permission: 'platform.audit.read',
  risk: 'read',
  scope: (input) => (input.locationId ? [input.locationId] : 'any'),
})

// ---- Zusatzfelder ---------------------------------------------------------------------------------------------------

export const zCustomField = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['text', 'number', 'integer', 'boolean', 'date', 'select', 'multiselect']),
  required: z.boolean(),
  /** nur bei select und multiselect */
  options: z.array(z.object({ value: z.string(), label: z.string() })),
  /** wo das Feld erscheint: 'list', 'form', 'document', 'export' */
  showIn: z.array(z.string()),
})

export const customFieldList = defineContract({
  name: 'platform.customField.list',
  title: 'Zusatzfelder anzeigen',
  description: 'Listet die Zusatzfelder einer Art von Datensatz (Artikel, Partner, Belege …) in Anzeigereihenfolge.',
  llm: {
    description:
      'Liefert die Zusatzfelder, die der Betrieb für eine Art von Datensatz eingerichtet hat (Schlüssel, Beschriftung, ' +
      'Typ, Auswahlwerte). Verwenden, bevor Zusatzfelder eines Artikels, Partners oder Belegs gesetzt werden.',
    keywords: ['Zusatzfeld', 'freies Feld', 'eigenes Feld', 'Merkmal'],
  },
  input: z.strictObject({ entityType: z.enum(['item', 'partner', 'document', 'document_line', 'lot', 'location']) }),
  output: z.object({ fields: z.array(zCustomField) }),
  permission: '@authenticated',
  risk: 'read',
})

// ---- Import -------------------------------------------------------------------------------------------------------------

const zImportIssue = z.object({
  /** Zeile in der Datei (1 = Kopfzeile); null = betrifft die ganze Datei */
  line: z.int().nullable(),
  column: z.string().optional(),
  message: z.string(),
  code: z.string().optional(),
})

const zImportColumn = z.object({
  key: z.string(),
  header: z.string(),
  required: z.boolean(),
  example: z.string().optional(),
  description: z.string().optional(),
})

const csvOptions = {
  /** Trennzeichen; Standard: aus der Kopfzeile ermittelt */
  delimiter: z.enum(['auto', ';', ',', 'tab']).default('auto'),
  /** Zeichensatz; Standard: BOM, sonst UTF-8, sonst Windows-1252 */
  encoding: z.enum(['auto', 'utf-8', 'windows-1252']).default('auto'),
}

export const importList = defineContract({
  name: 'platform.import.list',
  title: 'Importe anzeigen',
  description: 'Listet die Importe, die Sie ausführen dürfen, mit Spalten und einer Vorlage.',
  llm: { description: 'Listet verfügbare Datenimporte. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({}),
  output: z.object({
    imports: z.array(
      z.object({
        key: z.string(),
        title: z.string(),
        description: z.string().nullable(),
        columns: z.array(zImportColumn),
        /** leere CSV-Vorlage (Kopfzeile + Beispielzeile, Trennzeichen ;) */
        template: z.string(),
      }),
    ),
  }),
  permission: '@authenticated',
  risk: 'read',
  assistant: 'never',
})

export const importPreview = defineContract({
  name: 'platform.import.preview',
  title: 'Import prüfen',
  description: 'Liest die hochgeladene Datei, ordnet die Spalten zu und prüft jede Zeile – ohne etwas zu speichern.',
  llm: { description: 'Prüft eine hochgeladene Importdatei. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ key: z.string().min(3).max(80), fileId: zId, ...csvOptions }),
  output: z.object({
    key: z.string(),
    title: z.string(),
    delimiter: z.string(),
    encoding: z.string(),
    columns: z.array(z.object({ key: z.string(), header: z.string(), required: z.boolean(), found: z.boolean() })),
    /** Überschriften der Datei, die zu keiner Spalte passen (werden ignoriert) */
    unknownHeaders: z.array(z.string()),
    total: z.int().nonnegative(),
    valid: z.int().nonnegative(),
    invalid: z.int().nonnegative(),
    /** die ersten Fehler in Dateireihenfolge */
    errors: z.array(zImportIssue),
    errorsTruncated: z.boolean(),
    /** die ersten Zeilen, wie sie gelesen wurden */
    sample: z.array(z.object({ line: z.int(), values: z.record(z.string(), z.string()) })),
  }),
  permission: '@authenticated',
  risk: 'read',
  assistant: 'never',
})

export const importRun = defineContract({
  name: 'platform.import.run',
  title: 'Import starten',
  description:
    'Startet den Import der hochgeladenen Datei im Hintergrund. Jede Zeile wird einzeln ausgeführt; fehlerhafte Zeilen ' +
    'halten die übrigen nicht auf. Dieselbe Datei erneut zu importieren führt bereits übernommene Zeilen nicht noch einmal aus.',
  llm: { description: 'Startet einen Datenimport aus einer hochgeladenen Datei. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({
    key: z.string().min(3).max(80),
    fileId: zId,
    /** true: nur Probelauf – jede Zeile wird als Vorschau ausgeführt, nichts wird gespeichert */
    dryRun: z.boolean().default(false),
    ...csvOptions,
  }),
  output: z.object({ runId: zId }),
  permission: '@authenticated',
  risk: 'write',
  assistant: 'never',
})

export const IMPORT_STATUSES = ['pending', 'running', 'done', 'failed', 'cancelled'] as const

export const importStatus = defineContract({
  name: 'platform.import.status',
  title: 'Importlauf anzeigen',
  description: 'Zeigt Stand, Statistik und Fehlerliste eines Importlaufs.',
  llm: { description: 'Liest den Stand eines Importlaufs. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ runId: zId }),
  output: z.object({
    id: zId,
    key: z.string(),
    title: z.string(),
    status: z.enum(IMPORT_STATUSES),
    dryRun: z.boolean(),
    stats: z.object({
      total: z.int().nonnegative(),
      /** bearbeitete Zeilen (Fortschritt) */
      processed: z.int().nonnegative(),
      imported: z.int().nonnegative(),
      /** schon früher importiert – nicht erneut ausgeführt */
      skipped: z.int().nonnegative(),
      failed: z.int().nonnegative(),
      /** nur Probelauf: Zeilen ohne Fehler */
      valid: z.int().nonnegative(),
      durationMs: z.int().nonnegative(),
      fileName: z.string().nullable(),
      errorsTruncated: z.boolean(),
    }),
    errors: z.array(zImportIssue),
    createdAt: zTimestamp,
    startedAt: zTimestamp.nullable(),
    finishedAt: zTimestamp.nullable(),
  }),
  permission: '@authenticated',
  risk: 'read',
  assistant: 'never',
})

// ---- Verwaltung (Arbeitspaket „plattform“): Personen, Rollen & Rechte, Unternehmen, Anbindungen, Audit-Export und
//      Siegel, Datenschutz, System, eigenes Profil. Die Verträge liegen je Gegenstand unter ./contract/. ----
export * from './contract/users'
export * from './contract/roles'
export * from './contract/company'
export * from './contract/integrations'
export * from './contract/oversight'
export * from './contract/system'
export * from './contract/operations'
export * from './contract/mail'
export * from './contract/mailInbox'
export * from './contract/security'
export * from './contract/push'
